import React from 'react';
import { reportClientError } from '@/lib/monitoring/clientErrorReporter';
import { reportDebugError } from '@/features/debug-layer';

interface Props {
  children: React.ReactNode;
  /** Optional fallback. Defaults to a minimal recoverable surface. */
  fallback?: (args: { error: Error; reset: () => void }) => React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * AppErrorBoundary
 * ----------------
 * Top-level React Error Boundary. Reports caught errors to the monitoring
 * sink and renders a minimal recovery surface. Wraps the entire route tree.
 * Translation-free on purpose: it must keep working even if i18n itself
 * is the cause of the crash.
 */
export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    void reportClientError({
      message: error.message || 'Unknown render error',
      stack: error.stack ?? null,
      source: 'react_boundary',
      severity: 'fatal',
      context: {
        componentStack: (info?.componentStack ?? '').slice(0, 4000),
      },
    });

    // Zusätzlich für berechtigte Admins im Debug-Layer sichtbar machen.
    // Bewusst sanitisiert: kein Stack im User-UI, Stack nur als Payload im
    // (admin-gegateten) Layer. Greift nicht in Monitoring-Reporting ein.
    reportDebugError({
      scope: 'global',
      title: 'React Render-Error',
      description: error.message || 'Unknown render error',
      severity: 'error',
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      payload: {
        stack: error.stack ?? null,
        componentStack: (info?.componentStack ?? '').slice(0, 4000),
      },
      payloadVisibility: 'admin_safe',
    });
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    return (
      <div
        role="alert"
        className="min-h-[60vh] flex items-center justify-center px-4"
      >
        <div className="max-w-md w-full rounded-xl border border-border/60 bg-card p-6 text-center space-y-4">
          <h1 className="text-lg font-semibold">Etwas ist schiefgelaufen.</h1>
          <p className="text-sm text-muted-foreground">
            Wir haben das Problem automatisch gemeldet. Du kannst die Seite
            neu laden oder zur Startseite zurückkehren.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={() => {
                this.reset();
                if (typeof window !== 'undefined') window.location.reload();
              }}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Neu laden
            </button>
            <button
              type="button"
              onClick={() => {
                this.reset();
                if (typeof window !== 'undefined') window.location.assign('/');
              }}
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
            >
              Zur Startseite
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
